/**
 * Sticky Header Module
 *
 * This module provides sticky header functionality for the Sitewide Header.
 * It can be easily disabled by removing the script tag or setting
 * settings.sticky_header_enabled to false in theme settings.
 *
 * Features:
 * - Sticky header on scroll
 * - Optional: Hide announcement bar when sticky
 * - Optional: Show/hide on scroll direction
 * - Smooth transitions
 * - Mobile/Desktop breakpoint support
 *
 * To revert: Simply remove the <script> tag from theme-head.liquid
 */

(function() {
	'use strict';

	// Get settings from theme (passed via window.stickyHeaderSettings)
	const themeSettings = window.stickyHeaderSettings || {};

	// Configuration - merge theme settings with defaults
	const config = {
		headerSelector: '.shopify-section-group-sitewide-header',
		stickyClass: 'is-sticky',
		hideAnnouncementClass: 'hide-announcement',
		noShadowClass: 'no-shadow',
		mobileBreakpoint: 768,
		enabledDesktop: themeSettings.enabledDesktop !== false,
		enabledMobile: themeSettings.enabledMobile === true,
		scrollThreshold: themeSettings.threshold || 100,
		hideAnnouncement: themeSettings.hideAnnouncement !== false,
		showShadow: themeSettings.showShadow !== false,
	};

	let headerWrapper = null;
	let headerSections = [];
	let announcementSection = null;
	let navSection = null;
	let headerHeight = 0;
	let announcementHeight = 0;
	let lastScrollY = 0;
	let ticking = false;
	let isSticky = false;
	let stickyEnabled = false;

	/**
	 * Initialize the sticky header
	 */
	function init() {
		// Find ALL header section elements (they are siblings, not nested)
		const allHeaderSections = document.querySelectorAll(config.headerSelector);

		if (!allHeaderSections.length) {
			console.warn('Sticky Header: No header elements found');
			return;
		}

		// Filter to only visible sections (height > 0)
		headerSections = Array.from(allHeaderSections).filter(section => section.offsetHeight > 0);

		if (!headerSections.length) {
			console.warn('Sticky Header: No visible header sections found');
			return;
		}

		// Create a wrapper element around all visible header sections
		headerWrapper = document.createElement('div');
		headerWrapper.id = 'sticky-header-wrapper';
		headerWrapper.className = 'sticky-header-wrapper';

		// Insert wrapper before first visible header section
		const firstSection = headerSections[0];
		firstSection.parentNode.insertBefore(headerWrapper, firstSection);

		// Move all visible header sections into the wrapper
		headerSections.forEach(section => {
			headerWrapper.appendChild(section);
		});

		// Identify announcement vs nav sections
		// Announcement typically comes first, nav is last
		if (headerSections.length >= 2) {
			announcementSection = headerSections[0];
			navSection = headerSections[headerSections.length - 1];
		} else if (headerSections.length === 1) {
			navSection = headerSections[0];
		}

		// Calculate heights
		updateHeights();

		// Add CSS custom properties for heights
		document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
		document.documentElement.style.setProperty('--announcement-height', announcementHeight + 'px');

		// Add scroll listener
		window.addEventListener('scroll', onScroll, { passive: true });

		// Add resize listener to recalculate heights and check viewport
		window.addEventListener('resize', debounce(function() {
			updateHeights();
			checkViewport();
		}, 150));

		// Initial viewport check
		checkViewport();

		// Initial scroll check
		checkScroll();

		console.log('Sticky Header: Initialized with', headerSections.length, 'visible sections', config);
	}

	/**
	 * Check viewport and enable/disable sticky based on device settings
	 */
	function checkViewport() {
		const isMobile = window.innerWidth < config.mobileBreakpoint;
		const wasEnabled = stickyEnabled;

		// Determine if sticky should be enabled for current viewport
		if (isMobile) {
			stickyEnabled = config.enabledMobile;
		} else {
			stickyEnabled = config.enabledDesktop;
		}

		// If sticky was enabled and now disabled, remove sticky state
		if (wasEnabled && !stickyEnabled && isSticky) {
			removeSticky();
		}

		// If sticky is now enabled and we're past threshold, make sticky
		if (stickyEnabled && !isSticky) {
			const scrollY = window.scrollY || window.pageYOffset;
			if (scrollY > config.scrollThreshold) {
				makeSticky();
			}
		}
	}

	/**
	 * Update element heights
	 */
	function updateHeights() {
		if (headerWrapper) {
			headerHeight = headerWrapper.offsetHeight;
			document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
		}
		if (announcementSection) {
			announcementHeight = announcementSection.offsetHeight;
			document.documentElement.style.setProperty('--announcement-height', announcementHeight + 'px');
		}
	}

	/**
	 * Scroll event handler
	 */
	function onScroll() {
		if (!ticking) {
			window.requestAnimationFrame(function() {
				checkScroll();
				ticking = false;
			});
			ticking = true;
		}
	}

	/**
	 * Check scroll position and update header state
	 */
	function checkScroll() {
		// Skip if sticky is disabled for current viewport
		if (!stickyEnabled) {
			if (isSticky) {
				removeSticky();
			}
			return;
		}

		const scrollY = window.scrollY || window.pageYOffset;

		// Determine if header should be sticky
		if (scrollY > config.scrollThreshold) {
			if (!isSticky) {
				makeSticky();
			}
		} else {
			if (isSticky) {
				removeSticky();
			}
		}

		lastScrollY = scrollY;
	}

	/**
	 * Make header sticky
	 */
	function makeSticky() {
		isSticky = true;
		headerWrapper.classList.add(config.stickyClass);

		// Add hide-announcement class if enabled
		if (config.hideAnnouncement && announcementSection) {
			headerWrapper.classList.add(config.hideAnnouncementClass);
		}

		// Add no-shadow class if shadow is disabled
		if (!config.showShadow) {
			headerWrapper.classList.add(config.noShadowClass);
		}

		// Add padding to body to prevent content jump
		document.body.style.paddingTop = headerHeight + 'px';

		// Dispatch event for other scripts
		window.dispatchEvent(new CustomEvent('sticky-header-active', { detail: { isSticky: true } }));
	}

	/**
	 * Remove sticky state
	 */
	function removeSticky() {
		isSticky = false;
		headerWrapper.classList.remove(config.stickyClass);
		headerWrapper.classList.remove(config.hideAnnouncementClass);
		headerWrapper.classList.remove(config.noShadowClass);

		// Remove body padding
		document.body.style.paddingTop = '';

		// Dispatch event for other scripts
		window.dispatchEvent(new CustomEvent('sticky-header-active', { detail: { isSticky: false } }));
	}

	/**
	 * Debounce helper
	 */
	function debounce(func, wait) {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	}

	/**
	 * Public API
	 */
	window.modcoStickyHeader = {
		init: init,
		refresh: updateHeights,
		isSticky: function() { return isSticky; },
		destroy: function() {
			window.removeEventListener('scroll', onScroll);
			removeSticky();
			console.log('Sticky Header: Destroyed');
		}
	};

	// Auto-initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

})();
